import React, { useCallback } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { useNavigate } from "react-router-dom";
import { isNil } from "ramda";

import { AffinityForm, ConfirmationView, FormView } from "affinityForm";
import { useDryRunMutation, useSubmitMutation } from "affinityForm/helpers";
import { ButtonVariants, NavigateButton } from "affinityUI/elements/button";
import { Layout } from "affinityUI/structures/layout";
import { SettingsConfirmation, SettingsForm } from "components/views/settings";
import { hospitalConfigValidationSchema } from "components/views/settings/settingsForm/validationSchema";
import { SettingsFormContext } from "components/views/settings/settingsFormContext";
import { HospitalConfigurationType } from "components/views/settings/types";
import { CreateHospitalConfig, UpdateHospitalConfig, useGetHospitalConfigQuery } from "gql";
import { collectGarbage, composeUpdates, invalidatePatientDetails, invalidatePatientsList } from "gql/invalidations";
import { Restricted } from "lib/auth";

const varSetter = (hospitalConfig: HospitalConfigurationType) => ({
  hospitalConfig,
});

export const EditSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { showBoundary: onError } = useErrorBoundary();

  const update = composeUpdates([invalidatePatientsList, invalidatePatientDetails, collectGarbage]);

  const { data: hospitalConfigData, loading: loadingHospitalConfig } = useGetHospitalConfigQuery({ onError });
  const hospitalConfig = hospitalConfigData?.hospitalConfig;

  const validateUpdateHospitalConfig = useDryRunMutation(UpdateHospitalConfig, {
    varSetter,
    dataGetter: useCallback((response) => response?.data?.updateHospitalConfig, []),
  });
  const validateCreateHospitalConfig = useDryRunMutation(CreateHospitalConfig, {
    varSetter,
    dataGetter: useCallback((response) => response?.data?.createHospitalConfig, []),
  });
  const validateHospitalConfig = isNil(hospitalConfig?.id)
    ? validateCreateHospitalConfig
    : validateUpdateHospitalConfig;

  const executeUpdateHospitalConfig = useSubmitMutation(UpdateHospitalConfig, {
    varSetter,
    update,
    onSave: "/patients",
    dataGetter: useCallback((response) => response?.data?.updateHospitalConfig, []),
  });
  const executeCreateHospitalConfig = useSubmitMutation(CreateHospitalConfig, {
    varSetter,
    update,
    onSave: "/patients",
    dataGetter: useCallback((response) => response?.data?.createHospitalConfig, []),
  });
  const mutateHospitalConfig = isNil(hospitalConfig?.id) ? executeCreateHospitalConfig : executeUpdateHospitalConfig;

  const handleCancel = useCallback(() => {
    navigate(`/`);
  }, [navigate]);

  const isLoadingData = loadingHospitalConfig;

  return (
    <AffinityForm<HospitalConfigurationType>
      context={SettingsFormContext}
      initialData={hospitalConfig}
      validationSchema={hospitalConfigValidationSchema}
      asyncValidation={validateHospitalConfig}
      confirmHandler={mutateHospitalConfig}
    >
      <FormView>
        <Layout
          title="Settings"
          loading={isLoadingData}
          actions={
            <Restricted permits={["change_role"]}>
              <NavigateButton id="edit_roles" variant={ButtonVariants.SECONDARY} to="/settings/roles">
                Edit user roles
              </NavigateButton>
            </Restricted>
          }
        >
          <SettingsForm onCancel={handleCancel} />
        </Layout>
      </FormView>
      <ConfirmationView>
        <Layout title="Set and confirm" showBackBtn backLink=".." backLinkText="Back to settings form">
          <SettingsConfirmation onCancel={handleCancel} />
        </Layout>
      </ConfirmationView>
    </AffinityForm>
  );
};
